import ComboCounter from 'utils/ComboCounter';

import type { ParticleCore, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { DepartmentDeps } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

//Script group shared with the reference's DelayHelper group name — the
//per-lane triggers standing in for ParticleCounter closures + the swirl's
//stage DelayHelpers (and HomeScreens.allOn's internal 'screens'-grouped
//timers); cancelled wholesale on stopAsActiveScene/setupForWork, NEVER at
//phase transitions (unlike GROUP_SPACES — every screens script fires inside
//its own phase's duration in the reference, so transitions leave them alone):
export const GROUP_SCREENS = 'screens';

/**
 * ScreensContext — the shared plumbing the screens phase files work through
 * (the SpacesContext pattern): the pool range, the SHARED ComboCounter and
 * the reduce-first-pass flag, plus the one lane helper the phases share.
 *
 * Why these are context-owned and not phase-owned:
 *  - counters: the reference ScreensDepartment ticks ONE _comboCounter every
 *    frame across all phases; radialHalftoneImage and dropMachine clear() it
 *    on arm but SWIRL DOES NOT — its entries stack onto whatever dropMachine
 *    left pending (reference _doSwirl has no clear() call). A per-phase
 *    counter would break that carry-over, so the counter is shared state;
 *  - reduceFirstPass: written by the department at scene start (reference
 *    startAsActiveScene), read by BOTH the entries file's frame functions
 *    (the `*= 0.55` first-pass duration cut) and the halftone phase's combo
 *    timings (`reduce`), cleared once after the first arm (the department's
 *    onStarted hook — after both readers ran, matching the reference's
 *    read-then-clear order inside _nextScreensSection).
 *
 * ScreensRadialHalftone is deliberately NOT here: only the halftone phase
 * uses it and the reference builds a fresh instance per pass — it is
 * phase-owned (created in start(), nulled in hide()).
 */
export class ScreensContext {
	public readonly core: ParticleCore;
	public readonly range: PoolRange;
	public readonly deps: DepartmentDeps;

	/** The department-shared ComboCounter (see the class header). The
	 *  department ticks it every active frame and clears it on stop/park. */
	public readonly counters: ComboCounter;

	/** Reference ScreensDepartment._reduceFirstPass — true from scene start
	 *  until the first phase arm completes (cleared in the department's
	 *  onStarted hook). */
	public reduceFirstPass: boolean = false;

	constructor(core: ParticleCore, deps: DepartmentDeps) {
		this.core = core;
		this.deps = deps;
		this.range = core.pool('screens');
		this.counters = new ComboCounter();
	}

	/** position.set({x, y}, true) — rel write + HARD px write. The screens
	 *  pool is always in disableTimelineX mode (initScreensShape), whose hard
	 *  path is targetX = absX with NO xOffset (reference). */
	public setHardPosition(lane: number, x: number, y: number): void {
		const core = this.core;
		const sizes = this.deps.getSizes();

		core.relX[lane] = x;
		core.relY[lane] = y;
		core.tx[lane] = core.x[lane] = core.pTx[lane] = x * sizes.virtualWidth;
		core.ty[lane] = core.y[lane] = core.pTy[lane] = y * sizes.innerHeight;
	}
}
